import { Icon } from "@design-system/Icon";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { useMemo, type FC } from "react";

import { AiSuggestion } from "@app/shared/components/ai/AiSuggestion";
import { makeIndicatorLink } from "@app/shared/utils/annotations";

import { useGetIndicators } from "@app/screens/Frameworks/framework/Csrd/pillar/data";
import { ExtensionTag } from "@app/shared/components/ExtensionTag";
import { getFileFormatCategoryFromMimeType } from "@app/usecases/VersionUseCases/utils";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { type GetQuestionsQuery } from "@generated/client/graphql";
import { useParams } from "react-router-dom";
import { useQueryParam } from "use-query-params";
import { questionnaireParamsArgs } from "../../../../ctx";
import { useGetDocuments, useQuestionAndAnswerUpdate } from "../../data";
import { DocumentCitationTooltip } from "../DocumentCitationTooltip";
import { useQuestionsTabTranslations } from "../i18n";

export const AnswerAiSuggestedResources: FC<{
    question: GetQuestionsQuery["questions"][number];
    withButton?: boolean;
}> = ({ question, withButton }) => {
    const { org_uname } = useParams();
    const [activeGroupIdParams] = useQueryParam(
        ...questionnaireParamsArgs.group,
    );
    const { updateEvidenceFilesAsync } = useQuestionAndAnswerUpdate(
        question.id,
        question.answer?.id,
        question.questionnaireId,
        {
            groupId: activeGroupIdParams,
        },
    );
    const { t, keys } = useQuestionsTabTranslations();

    const aiSuggestion = question?.answer?.aiSuggestion;

    const { documentCitations, indicatorCitations, aiSuggestionSuccess } =
        useMemo(() => {
            const documentCitations = aiSuggestion?.documentCitations ?? [];
            const indicatorCitations = aiSuggestion?.indicatorCitations ?? [];
            const success = aiSuggestion?.success;

            return {
                documentCitations,
                indicatorCitations,
                aiSuggestionSuccess: success ?? false,
            };
        }, [question?.answer?.aiSuggestion, t]);

    const indicatorIds = indicatorCitations
        .map((c: { indicatorId: string }) => {
            const split = c.indicatorId.split("_");
            return split?.length ? split[0] : null;
        })
        .filter((id) => typeof id === "string");

    const { indicators: suggestedIndicators } = useGetIndicators(indicatorIds);

    const { documents: suggestedDocuments } = useGetDocuments(
        documentCitations?.map((c: { documentId: string }) => c.documentId) ??
            [],
    );

    const handleInsertDocumentLinkClick = (documentId: string) => {
        return () => {
            updateEvidenceFilesAsync({
                fileIds: suggestedDocuments
                    .filter((doc) => doc.id === documentId)
                    ?.map(
                        (document) =>
                            document?.lastApprovedVersion?.finalFile?.id,
                    )
                    .filter((fileId) => typeof fileId === "string"),
            });
        };
    };

    return !!suggestedDocuments?.length || !!suggestedIndicators?.length ? (
        <FlexCol>
            {!!suggestedDocuments?.length && (
                <AiSuggestion.ReferenceBlock
                    title={t(keys.ai_suggestion_document_references)}
                >
                    <FlexCol gap="1.5">
                        {suggestedDocuments?.map((document) => (
                            <FlexRow
                                w="full"
                                justifyContent="between"
                                alignItems="center"
                                key={document.id}
                            >
                                <FlexRow
                                    alignItems="center"
                                    justifyContent="between"
                                    gap="3"
                                >
                                    <ExtensionTag
                                        name={getFileFormatCategoryFromMimeType(
                                            document?.lastApprovedVersion
                                                ?.finalFile?.mimeType ||
                                                "application/pdf",
                                        )}
                                    />

                                    <DocumentCitationTooltip
                                        document={document}
                                        documentCitation={documentCitations.find(
                                            (citation) =>
                                                citation.documentId ===
                                                document.id,
                                        )}
                                    />
                                </FlexRow>

                                {aiSuggestionSuccess && withButton && (
                                    <Box
                                        onClick={handleInsertDocumentLinkClick(
                                            document.id,
                                        )}
                                        className="cursor-pointer p-1 text-primary"
                                    >
                                        <Button variant="outlined" size="sm">
                                            <Text variant="body-sm-bold">
                                                {t(keys.ai_suggestion_confirm)}
                                            </Text>
                                            <Button.Icon
                                                name="check"
                                                size="sm"
                                            />
                                        </Button>
                                    </Box>
                                )}
                            </FlexRow>
                        ))}
                    </FlexCol>
                </AiSuggestion.ReferenceBlock>
            )}
            {!!suggestedIndicators?.length && (
                <AiSuggestion.ReferenceBlock
                    title={t(keys.ai_suggestion_reporting)}
                >
                    <FlexCol gap="2">
                        {suggestedIndicators?.map(
                            (indicator: { id: string; name: string }) => (
                                <FlexRow key={indicator?.id}>
                                    <a
                                        href={makeIndicatorLink(
                                            indicator?.name,
                                            org_uname,
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:text-beavrGreen"
                                    >
                                        <FlexRow gap="2" alignItems="center">
                                            <Icon name="ruler" size="sm" />
                                            <Text variant="body-bold">
                                                {indicator?.name}
                                            </Text>
                                        </FlexRow>
                                    </a>
                                </FlexRow>
                            ),
                        )}
                    </FlexCol>
                </AiSuggestion.ReferenceBlock>
            )}
        </FlexCol>
    ) : null;
};
